import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { toast } from 'react-toastify';
import PeopleIcon from '@material-ui/icons/People';
import SaveAlt from '@material-ui/icons/SaveAlt';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Paper from '~/components/Paper';
import Button from '~/components/Button';
import Table from '~/components/Table/Table';
import GridItem from '~/components/GridItem';
import InputDate from '~/components/Input/InputDate';
import GridContainer from '~/components/GridContainer';
import LinearProgress from '~/components/LinearProgress';
import tableStyles from '~/components/Table/Table/styles';
import InputControl from '~/components/Input/InputControl';
import TablePagination from '~/components/Table/TablePagination';
import Request from '~/providers/Request';
import toInt from '~/utils/Number/toInt';
import isPresent from '~/utils/isPresent';
import csvGenerator from '~/utils/csvGenerator';
import xlsxGenerator from '~/utils/xlsxGenerator';
import { URL } from '~/config/uri';
import InputSwitch from '~/components/Input/InputSwitch';
import Chip from '@material-ui/core/Chip';
import LinkMaterial from '@material-ui/core/Link';
import { showPerson } from '~/config/routes';

function ReportsMercadoPago() {
  const { t } = useTranslation();
  const classesTable = tableStyles();
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [registers, setRegisters] = useState(null);
  const [totalReports, setTotalReports] = useState(0);
  const [dataInit, setDataInit] = useState(moment().startOf('month').format());
  const [dataEnd, setDataEnd] = useState(moment().endOf('month').format());
  const [notUsedDaysAfter, setNotUsedDaysAfter] = useState(false);
  const [notRenewedAfterAcquire, setNotRenewedAfterAcquire] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [constantsMP, setConstantsMP] = useState({});
  const trAttr = (attr) => t(`reportsMercadoPago.attributes.${attr}`);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);

  async function generateFile(filters, type) {
    const report_name = `mercadopago-${moment().format('DD-MM-YYYY-HH-mm')}`;
    try {
      const rows = [];

      rows.push([trAttr('reports').toUpperCase()]);
      rows.push(['']);

      rows.push(['Data Inicial do Relatório', `${moment(dataInit).format('DD/MM/YYYY HH:mm')}`]);
      rows.push(['Data Final do Relatório', `${moment(dataEnd).format('DD/MM/YYYY HH:mm')}`]);
      rows.push(['']);

      rows.push([
        'ID',
        trAttr('person_name'),
        trAttr('cpf'),
        trAttr('value'),
        trAttr('payment_type'),
        trAttr('installments'),
        trAttr('product_description'),
        trAttr('status'),
        trAttr('created_at'),
        trAttr('updated_at'),
      ]);

      reports.map((row) => {
        rows.push([
          row.id,
          row.user.name,
          row.user.cpf,
          formatAmount(row.amount*100),
          constantsMP?.payment_type[row.payment_type_id],
          row.installments,
          row.product?.description,
          constantsMP?.status[row.status]?.title,
          moment(row.created_at).format('DD/MM/YYYY HH:mm:ss'),
          moment(row.updated_at).format('DD/MM/YYYY HH:mm:ss')
        ]);

        return row;
      });

      rows.push(['']);

      rows.push(['Número de vendas', reports.length]);
      rows.push(['Valor total de venda(R$)', someTotalAmount()]);

      rows.push(['']);
      rows.push(['Relatório gerado em', `${moment().format('L')} - ${moment().format('LT')}`]);

      if (type === 'csv') csvGenerator(`${report_name}.csv`, [...rows]);
      else xlsxGenerator(`${report_name}.xlsx`, [...rows]);

      toast.success(`Arquivo ${type.toLocaleUpperCase()} gerado com sucesso!`);

      const totalPages = toInt(registers.meta.total) / registers.meta.perPage;
      if (registers.meta.page < totalPages) {
        filters.page += 1;
        filters.report_name = report_name;
        generateFile(filters, type);
      }
    } catch (error) {
      console.log(error);
      toast.error(
        `Não foi possível gerar o arquivo ${type.toLocaleUpperCase()}`
      );
    }
  }

  function onExport(type) {
    const filters = {
      qs: '',
      page: 1,
      per_page: 50000,
    };

    generateFile(filters, type);
  }

  async function getReports() {
    setLoading(true);
    const filters = {
      start: dataInit,
      end: dataEnd,
      not_used_days_after: notUsedDaysAfter,
      not_renewed_after_acquire: notRenewedAfterAcquire,
      page: page + 1,
      per_page: rowsPerPage,
    };

    Request.get('/admin/report/mercadopago', filters, { normalizeJSON: false })
      .then((response) => {
        setLoading(false);
        setRegisters(response);
        setReports(response.data);
        setPage(toInt(response.meta.page) - 1);
        setTotalReports(toInt(response.meta.total));
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  }

  async function getConstantsMP() {
    setLoading(true);
    const response = await fetch(`${URL}/public/mp/constants`, { method: 'GET' });
    const data = await response.json();
    setConstantsMP(data);
  }

  async function fetchParams() {
    const url = new URLSearchParams(window.location.search);
    const start = url.get('start');
    const end = url.get('end');

    if (isPresent(start)) {
      const startDate = moment(start);

      if (startDate.isValid()) {
        setDataInit(startDate.format());
      }
    }

    if (isPresent(end)) {
      const endDate = moment(end);

      if (endDate.isValid()) {
        setDataEnd(endDate.format());
      }
    }
  }

  useEffect(() => {
    if (dataInit && dataEnd) {
      getReports();
      getConstantsMP();
    }
  }, [
    notUsedDaysAfter,
    notRenewedAfterAcquire,
    page,
    rowsPerPage,
    dataInit,
    dataEnd,
  ]);

  useEffect(() => {
    fetchParams();
  }, []);

  const formatAmount = (value) => {
    const valueString = value.toString();
    const position = valueString.length - 2;
    const output = [valueString.slice(0, position), '.', valueString.slice(position)].join('');
    return Number(output).toLocaleString('pt-BR', { minimumFractionDigits: 2, style: 'currency', currency: 'BRL' });
  };

  const someTotalAmount = () => {
    let some = 0;
    reports.map((item) => { some += item.amount*100; });
    return formatAmount(some);
  };

  return (
    <>
      <Paper
        Icon={PeopleIcon}
        iconColor="blue"
        title={trAttr('reports')}
        subtitle={trAttr('reports_description')}
      >
        <GridContainer>
          <GridItem xs={12} sm={3} md={3} lg={3} xl={3}>
            <InputControl
              value={dataInit}
              name="date"
              label={t('reportsMercadoPago.extras.date_init')}
              inputComponent={InputDate}
              InputProps={{
                type: 'date',
              }}
              onChange={(date) => {
                setDataInit(moment(date).startOf('day').format());
              }}
            />
          </GridItem>
          <GridItem xs={12} sm={3} md={3} lg={3} xl={3}>
            <InputControl
              value={dataEnd}
              name="date"
              label={t('reportsMercadoPago.extras.date_end')}
              inputComponent={InputDate}
              InputProps={{
                type: 'date',
              }}
              onChange={(date) =>
                setDataEnd(moment(date).endOf('day').format())
              }
            />
          </GridItem>
          <GridItem align="right">
            <Button
              color="second"
              onClick={() => {
                onExport('csv');
              }}
            >
              {t('action.export_csv')} <SaveAlt />
            </Button>
            <Button
              style={{ marginLeft: 10 }}
              color="orange"
              onClick={() => {
                onExport('xlsx');
              }}
            >
              {t('action.export_xlsx')} <SaveAlt />
            </Button>
          </GridItem>
          <GridItem xs={12}>
            <InputSwitch
              label={t('reportsMercadoPago.extras.notUsedDaysAfter')}
              value={notUsedDaysAfter}
              onChange={setNotUsedDaysAfter}
            />
            <InputSwitch
              label={t('reportsMercadoPago.extras.notRenewedAfterAcquire')}
              value={notRenewedAfterAcquire}
              onChange={setNotRenewedAfterAcquire}
            />
          </GridItem>
        </GridContainer>

        <LinearProgress loading={loading} />

        <Table
          emptyData={reports.length === 0}
          loading={loading}
          columns={
            <>
              <TableCell className={classesTable.cell}>ID</TableCell>
              <TableCell className={classesTable.cell}>{trAttr('person_name')}</TableCell>
              <TableCell className={classesTable.cell}>{trAttr('cpf')}</TableCell>
              <TableCell className={classesTable.cell}>{trAttr('value')}</TableCell>
              <TableCell className={classesTable.cell}>{trAttr('payment_type')}</TableCell>
              <TableCell className={classesTable.cell}>{trAttr('installments')}</TableCell>
              <TableCell className={classesTable.cell}>{trAttr('product_description')}</TableCell>
              <TableCell className={classesTable.cell}>{trAttr('status')}</TableCell>
              <TableCell className={classesTable.cell}>{trAttr('created_at')}</TableCell>
              <TableCell className={classesTable.cell}>{trAttr('updated_at')}</TableCell>
            </>
          }
        >
          {isPresent(constantsMP) && reports.length > 0 && reports.map((row) => {
            return (
              <TableRow key={`${row.id}-${row.report_name}`} hover className={classesTable.row}>
                <TableCell>{row.id}</TableCell>
                <TableCell>
                  <Chip
                    label={row.user?.name}
                    component={LinkMaterial}
                    href={showPerson.reverse({ id: row.user?.id })}
                    style={{ cursor: 'pointer' }}
                    target="_blank"
                    color="primary"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{row.user?.cpf}</TableCell>
                <TableCell>{formatAmount(row.amount*100)}</TableCell>
                <TableCell>{constantsMP?.payment_type[row.payment_type_id]}</TableCell>
                <TableCell>{row.installments}</TableCell>
                <TableCell>{row.product?.description}</TableCell>
                <TableCell>{constantsMP?.status[row.status]?.title}</TableCell>
                <TableCell>{moment(row.created_at).format('DD/MM/YYYY HH:mm:ss')}</TableCell>
                <TableCell>{moment(row.updated_at).format('DD/MM/YYYY HH:mm:ss')}</TableCell>
              </TableRow>
            );
          })}
        </Table>

        <LinearProgress loading={loading} />

        <TablePagination
          count={totalReports}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Paper>
    </>
  );
}

export default ReportsMercadoPago;
